import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { useLocale } from '@app/core/hooks/useLocale';
import {
  abbreviatedValueFormatter,
  numberValueFormatter,
  toPercentageUsingFormatter,
} from '@app/shared/formatters';
import { DenominationMultiplier, OwnerType } from '@app/shared/models/contracts/enums/shared-enums';
import { FC } from 'react';
import styles from '@app/modules/projects/dashboard/shared/per-year-capital-structure/per-year-capital-structure-chart.module.scss';
import { PerYearCapitalStructureChartYearData } from '@app/modules/projects/dashboard/shared/per-year-capital-structure/per-year-capital-structure-transformers';

const PARENT_CLASSNAME = 'total-equity-chart';

interface ForecastTooltipProps {
  forecastData: PerYearCapitalStructureChartYearData;
  isEntryTooltip?: boolean;
}

export const PerYearCapitalStructureForecastTooltip: FC<ForecastTooltipProps> = ({
  forecastData,
  isEntryTooltip = false,
}) => {
  const { l } = useLocale();
  const denomination = useAppSelector((state) => state.project.projectDraft.details.denomination);
  const multiplier = DenominationMultiplier[denomination];
  const { weightedEv, presentValueOfTotalEquity, proceedsByOwner, sponsorMoM, sponsorIRR } =
    forecastData;

  const validOwnerEntries = Object.entries(proceedsByOwner).filter(
    ([, value]) => value != null && value !== 0
  );

  return (
    <div key={'forecast-tooltip-' + forecastData.forecastDate}>
      <dl className="definition-list">
        <div className="definition-list__row">
          <div className="definition-list__row-item">
            <dt>{isEntryTooltip ? l('_InitialEV') : l('_WeightedEV')}</dt>
            <dd>{abbreviatedValueFormatter({ value: weightedEv * multiplier })}</dd>
          </div>
          <div className="definition-list__row-item">
            <dt>
              {isEntryTooltip ? l('_InitialInvestment') : l('_WeightedTotalAttributableEquity')}
            </dt>
            <dd>
              {abbreviatedValueFormatter({
                value: presentValueOfTotalEquity * multiplier,
              })}
            </dd>
          </div>
        </div>
        <div className="definition-list__row">
          {validOwnerEntries.map(([ownerKey, value]) => (
            <div key={ownerKey} className="definition-list__row-item">
              <dt>
                {isEntryTooltip
                  ? l('_TotalOwnerInvestment', {
                      Owner: OwnerType[ownerKey as keyof typeof OwnerType],
                    })
                  : l('_TotalOwnerProceeds', {
                      Owner: OwnerType[ownerKey as keyof typeof OwnerType],
                    })}
              </dt>
              <dd>{abbreviatedValueFormatter({ value: value * multiplier })}</dd>
            </div>
          ))}
        </div>
        {!isEntryTooltip && (
          <div className="definition-list__row">
            {sponsorMoM != null && (
              <div className="definition-list__row-item">
                <dt>{l('_SponsorMoM')}</dt>
                <dd>
                  {numberValueFormatter({ value: sponsorMoM, fraction: 2, valueSuffix: 'x' })}
                </dd>
              </div>
            )}
            {sponsorIRR != null && (
              <div className="definition-list__row-item">
                <dt>{l('_SponsorIRR')}</dt>
                <dd>{toPercentageUsingFormatter(sponsorIRR)}</dd>
              </div>
            )}
          </div>
        )}
      </dl>
      <div className={styles[`${PARENT_CLASSNAME}__forecast-tooltip-footer`]}>
        <b>{l('_ClickToEnlarge')}</b>
      </div>
    </div>
  );
};
